import * as React from "react";
import { Platform, View } from "react-native";
import { useRouter } from "expo-router";

import {
  AccountActionsContext,
  ModalType,
} from "~/components/AccountActions/context";
import { CollectionDialog } from "~/components/CollectionDialog.web";
import { ConfirmationDialog } from "~/components/confirmation/Dialog.web";
import { CopySaveDialog } from "~/components/CopySave/Dialog.web";
import { MoveSaveDialog } from "~/components/MoveSave/Dialog.web";
import SaveFormDialog from "~/components/SaveForm/SaveForm.dialog.web";
import { TagDialog } from "~/components/Tag/Dialog.web";
import { useCurrentAccount } from "~/hooks/useCurrentAccount";
import { isWeb } from "~/lib/constants";
import { SpaceForm } from "../SpaceForm";
import { CreateTeamForm } from "../Team/CreateTeamForm";
import { Modal } from "./modal";

export function AccountActionsProvider(props: { children: React.ReactNode }) {
  const [stack, dispatch] = React.useReducer(modalStackReducer, new Map());
  const { slug } = useCurrentAccount();
  const { push } = useRouter();
  const tempState = React.useRef<unknown>(null);

  // const { isDesktop } = useIsDesktop();

  // @ts-expect-error TODO: find out how to please TS gods
  const getTempState: AccountActionsContext["getTempState"] =
    React.useCallback(() => {
      return tempState.current as unknown as ReturnType<
        AccountActionsContext["getTempState"]
      >;
    }, []);

  // This feels wrong tbh
  const removeModalByType: AccountActionsContext["removeModalByType"] =
    React.useCallback((modalType) => {
      dispatch({
        type: "REMOVE_MODAL_BY_TYPE",
        modalType,
      });
    }, []);

  const openAddSpace = React.useCallback(() => {
    if (isWeb) {
      dispatch({
        type: "ADD_MODAL",
        key: generateShortId(),
        value: { type: "addSpace", params: undefined },
      });

      return;
    }

    push({
      pathname: "/(app)/(authenticated)/[account]/spaceForm",
      params: {
        account: slug,
      },
    });
  }, [push, slug]);

  const openEditSpace: AccountActionsContext["openEditSpace"] =
    React.useCallback((params) => {
      dispatch({
        type: "ADD_MODAL",
        key: generateShortId(),
        value: { type: "editSpace", params },
      });
    }, []);

  const openAddCollection: AccountActionsContext["openAddCollection"] =
    React.useCallback(
      (params) => {
        if (isWeb) {
          dispatch({
            type: "ADD_MODAL",
            key: generateShortId(),
            value: { type: "addCollection", params },
          });

          return;
        }

        push({
          pathname: "/(app)/(authenticated)/[account]/collection",
          params: {
            account: slug,
          },
        });
      },
      [push, slug],
    );

  const openEditCollection: AccountActionsContext["openEditCollection"] =
    React.useCallback(
      (params) => {
        if (isWeb) {
          dispatch({
            type: "ADD_MODAL",
            key: generateShortId(),
            value: { type: "editCollection", params: params },
          });

          return;
        }

        push({
          pathname: "/(app)/(authenticated)/[account]/collection/form",
          params: {
            account: slug,
            spaceId: params.space.id,
            collectionId: params.collection.id,
          },
        });
      },
      [push, slug],
    );

  const openAddSave: AccountActionsContext["openAddSave"] = React.useCallback(
    (params) => {
      tempState.current = params;

      if (isWeb) {
        dispatch({
          type: "ADD_MODAL",
          key: generateShortId(),
          value: { type: "addSave", params: params },
        });

        return;
      }

      push({
        pathname: "/(app)/(authenticated)/[account]/link",
        params: {
          type: params.type ?? "URL",
          account: slug,
        },
      });
    },
    [push, slug],
  );

  const openEditSave: AccountActionsContext["openEditSave"] = React.useCallback(
    (params) => {
      tempState.current = params;

      if (isWeb) {
        dispatch({
          type: "ADD_MODAL",
          key: generateShortId(),
          value: { type: "editSave", params: params },
        });

        return;
      }

      push({
        pathname: "/(app)/(authenticated)/[account]/link/form",
        params: {
          account: slug,
          saveId: params.id!,
          spaceId: params.id!,
          type: params.type,
          asset: "",
        },
      });
    },
    [push, slug],
  );

  const openCopySave: AccountActionsContext["openCopySave"] = React.useCallback(
    (params) => {
      tempState.current = params;

      if (isWeb) {
        dispatch({
          type: "ADD_MODAL",
          key: generateShortId(),
          value: { type: "copySave", params: params },
        });

        return;
      }

      push({
        pathname: "/(app)/(authenticated)/[account]/copyTo",
        params: {
          account: slug,
          asset: "",
        },
      });
    },
    [push, slug],
  );

  const openMoveSave: AccountActionsContext["openMoveSave"] = React.useCallback(
    (params) => {
      tempState.current = params;

      if (isWeb) {
        dispatch({
          type: "ADD_MODAL",
          key: generateShortId(),
          value: { type: "moveSave", params: params },
        });

        return;
      }

      push({
        pathname: "/(app)/(authenticated)/[account]/moveTo",
        params: {
          account: slug,
          asset: "",
        },
      });
    },
    [push, slug],
  );

  const openAddTag: AccountActionsContext["openAddTag"] = React.useCallback(
    (params) => {
      tempState.current = params;

      if (isWeb) {
        dispatch({
          type: "ADD_MODAL",
          key: generateShortId(),
          value: { type: "addTag", params: params },
        });

        return;
      }

      push({
        pathname: "/(app)/(authenticated)/[account]/moveTo",
        params: {
          account: slug,
          asset: "",
        },
      });
    },
    [push, slug],
  );

  const openEditTag: AccountActionsContext["openEditTag"] = React.useCallback(
    (params) => {
      tempState.current = params;

      if (isWeb) {
        dispatch({
          type: "ADD_MODAL",
          key: generateShortId(),
          value: { type: "editTag", params: params },
        });

        return;
      }

      push({
        pathname: "/(app)/(authenticated)/[account]/moveTo",
        params: {
          account: slug,
          asset: "",
        },
      });
    },
    [push, slug],
  );

  const openCreateTeam: AccountActionsContext["openCreateTeam"] =
    React.useCallback((params) => {
      dispatch({
        type: "ADD_MODAL",
        key: generateShortId(),
        value: { type: "createTeam", params },
      });
    }, []);

  const ctxValue = React.useMemo(
    () => ({
      openAddSpace,
      openEditSpace,
      openAddCollection,
      openEditCollection,
      openAddSave,
      openEditSave,
      openCopySave,
      openMoveSave,
      getTempState,
      removeModalByType,
      openAddTag,
      openEditTag,
      openCreateTeam,
    }),
    [
      openAddSpace,
      openEditSpace,
      openAddCollection,
      openEditCollection,
      openAddSave,
      openEditSave,
      openCopySave,
      openMoveSave,
      getTempState,
      removeModalByType,
      openAddTag,
      openEditTag,
      openCreateTeam,
    ],
  );

  const shouldShowConfirmationDialog = React.useSyncExternalStore(
    ConfirmationStore.subscribe,
    ConfirmationStore.getSnapshot,
  );

  return (
    <AccountActionsContext.Provider value={ctxValue}>
      {shouldShowConfirmationDialog && (
        <ConfirmationDialog
          onConfirm={ConfirmationStore.onConfirm}
          onCancel={ConfirmationStore.onCancel}
        />
      )}
      {props.children}
      <View>
        {Array.from(stack.entries()).map(([key, value]) => {
          switch (value.type) {
            case "addSpace":
              return (
                <Modal
                  key={key}
                  header="New Workspace"
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                >
                  {({ dismiss }) => (
                    <SpaceForm
                      inBottomSheet={Platform.OS !== "web"}
                      onSuccess={dismiss}
                    />
                  )}
                </Modal>
              );
            case "editSpace":
              return (
                <Modal
                  key={key}
                  header="Edit Workspace"
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                >
                  {({ dismiss }) => (
                    <SpaceForm
                      inBottomSheet={Platform.OS !== "web"}
                      spaceId={value.params.spaceId}
                      onSuccess={dismiss}
                    />
                  )}
                </Modal>
              );
            case "addCollection": {
              return (
                <CollectionDialog
                  key={key}
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                  step={
                    value.params?.spaceId
                      ? {
                          step: "form",
                          props: {
                            spaceId: value.params.spaceId,
                          },
                        }
                      : undefined
                  }
                />
              );
            }
            case "editCollection": {
              return (
                <CollectionDialog
                  key={key}
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                  step={{
                    step: "form",
                    props: {
                      spaceId: value.params.space.id,
                      collectionId: value.params.collection.id,
                    },
                  }}
                />
              );
            }
            case "addSave": {
              return (
                <SaveFormDialog
                  key={key}
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                  {...value.params}
                />
              );
            }
            case "editSave": {
              return (
                <SaveFormDialog
                  key={key}
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                  {...value.params}
                />
              );
            }
            case "moveSave": {
              return (
                <MoveSaveDialog
                  key={key}
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                  saveIds={[value.params.save.id]}
                  srcSpaceId={value.params.srcSpaceId}
                />
              );
            }
            case "copySave": {
              return (
                <CopySaveDialog
                  key={key}
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                  saveId={value.params.save.id}
                  srcSpaceId={value.params.srcSpaceId}
                />
              );
            }
            case "addTag":
              return (
                <TagDialog
                  key={key}
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                  {...value.params}
                />
              );

            case "editTag":
              return (
                <TagDialog
                  key={key}
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                  {...value.params}
                />
              );
            case "createTeam":
              return (
                <Modal
                  key={key}
                  header="New Team"
                  onDismiss={() => dispatch({ type: "REMOVE_MODAL", key })}
                >
                  {({ dismiss }) => (
                    <CreateTeamForm
                      inBottomSheet={Platform.OS !== "web"}
                      onDismiss={dismiss}
                      onSuccess={(slug) =>
                        push({
                          pathname:
                            "/(app)/(authenticated)/[account]/(account)",
                          params: {
                            account: slug,
                          },
                        })
                      }
                    />
                  )}
                </Modal>
              );
            default:
              const unhandled: never = value;
              console.error("Unhandled modal type", JSON.stringify(unhandled));
              return null;
          }
        })}
      </View>
    </AccountActionsContext.Provider>
  );
}

type ModalStack = Map<string, ModalType>;

type ModalStackAction =
  | { type: "ADD_MODAL"; key: string; value: ModalType }
  | { type: "REMOVE_MODAL"; key: string }
  | { type: "REMOVE_MODAL_BY_TYPE"; modalType: ModalType["type"] };

function modalStackReducer(state: ModalStack, action: ModalStackAction) {
  switch (action.type) {
    case "ADD_MODAL":
      return new Map([...state, [action.key, action.value]]);
    case "REMOVE_MODAL":
      return new Map([...state].filter(([key, _]) => key !== action.key));
    case "REMOVE_MODAL_BY_TYPE":
      return new Map(
        [...state].filter(([_, type]) => type.type !== action.modalType),
      );
    default:
      throw new Error("invalid action, modalStackReducer");
  }
}

function generateShortId() {
  return Math.random().toString(36).substring(2, 9);
}

export const ConfirmationStore = {
  _isVisible: false,
  onConfirm: () => {},
  onCancel: () => {},

  makeVisible: ({
    onConfirm,
    onCancel,
  }: {
    onConfirm: () => void;
    onCancel?: () => void;
  }) => {
    ConfirmationStore._isVisible = true;
    ConfirmationStore.onConfirm = () => {
      onConfirm();
      ConfirmationStore.makeInvisible();
    };
    ConfirmationStore.onCancel = () => {
      onCancel?.();
      ConfirmationStore.makeInvisible();
    };

    ConfirmationStore._listeners.forEach((cb) => cb(true));
  },

  makeInvisible: () => {
    ConfirmationStore._isVisible = false;
    ConfirmationStore._listeners.forEach((cb) => cb(false));
  },

  _listeners: [] as ((value: boolean) => void)[],
  subscribe: (callback: (value: boolean) => void) => {
    ConfirmationStore._listeners.push(callback);
    return () => {
      ConfirmationStore._listeners = ConfirmationStore._listeners.filter(
        (cb) => cb !== callback,
      );
    };
  },
  getSnapshot: () => ConfirmationStore._isVisible,
};
